import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, X, Star, Loader2, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";

interface ToolTag {
  id: string;
  name: string;
  category: string;
  is_custom?: boolean;
  library_resource_id?: string | null;
}

interface ContributorTool {
  id: string;
  proficiency: string | null;
  tool_tag_id: string;
  tool_tags: ToolTag;
}

interface ToolSelectorProps {
  userId: string;
  tools: ContributorTool[];
  onUpdate: () => void;
}

const PROFICIENCY_LEVELS = [
  { value: "beginner", label: "Beginner", stars: 1 },
  { value: "intermediate", label: "Intermediate", stars: 2 },
  { value: "expert", label: "Expert", stars: 3 },
];

const CATEGORY_ORDER = ["design", "productivity", "development", "communication", "ai", "other"];

const normalizeEntryName = (input: string): string => {
  return input
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const ToolSelector: React.FC<ToolSelectorProps> = ({ userId, tools, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const queryClient = useQueryClient();
  const { organization } = useOrganization();

  // Fetch all available tool tags
  const { data: allTools } = useQuery({
    queryKey: ["tool-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tool_tags")
        .select("*")
        .order("category")
        .order("name");
      
      if (error) throw error;
      return data as ToolTag[];
    },
  });

  // Add tool mutation
  const addToolMutation = useMutation({
    mutationFn: async ({ toolTagId, proficiency }: { toolTagId: string; proficiency: string }) => {
      const { error } = await supabase
        .from("contributor_tools")
        .insert({
          profile_id: userId,
          tool_tag_id: toolTagId,
          proficiency,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      setSearchInput("");
      setOpen(false);
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to add tool: ${error.message}`);
    },
  });

  // Update proficiency mutation
  const updateProficiencyMutation = useMutation({
    mutationFn: async ({ toolId, proficiency }: { toolId: string; proficiency: string }) => {
      const { error } = await supabase
        .from("contributor_tools")
        .update({ proficiency })
        .eq("id", toolId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to update proficiency: ${error.message}`);
    },
  });

  // Create custom tool mutation with auto-submit to resource library
  const createCustomToolMutation = useMutation({
    mutationFn: async (toolName: string) => {
      const normalizedName = normalizeEntryName(toolName);
      
      // Check if tool already exists (case-insensitive)
      const { data: existing } = await supabase
        .from("tool_tags")
        .select("id")
        .ilike("name", normalizedName)
        .maybeSingle();
      
      if (existing) {
        // Add existing tool to user
        await supabase.from("contributor_tools").insert({
          profile_id: userId,
          tool_tag_id: existing.id,
          proficiency: "intermediate",
        });
        return existing.id;
      }
      
      // Insert new custom tool
      const { data: newTool, error } = await supabase
        .from("tool_tags")
        .insert({
          name: normalizedName,
          category: "other",
          is_custom: true,
        })
        .select("id")
        .single();
      
      if (error) throw error;
      
      // Add to user's tools
      await supabase.from("contributor_tools").insert({
        profile_id: userId,
        tool_tag_id: newTool.id,
        proficiency: "intermediate",
      });
      
      // Check if this tool exists in the library
      const { data: libraryItem } = await supabase
        .from("library_items")
        .select("id")
        .ilike("title", `%${normalizedName}%`)
        .eq("type", "tool")
        .maybeSingle();
      
      // If not in library, create a pending resource for review
      if (!libraryItem && organization?.id) {
        await supabase.from("pending_resources").insert({
          submission_type: "auto_tool_suggestion",
          text_input: `User-suggested tool: ${normalizedName}. Please create a resource page for this tool.`,
          urls: [{ url: `https://www.google.com/search?q=${encodeURIComponent(normalizedName)}` }],
          user_id: userId,
          organization_id: organization.id,
          status: "pending",
        });
        toast.info(`Added! We'll create a resource page for ${normalizedName}.`, { duration: 4000 });
      } else if (libraryItem) {
        // Link the tool to the library item
        await supabase
          .from("tool_tags")
          .update({ library_resource_id: libraryItem.id })
          .eq("id", newTool.id);
      }
      
      // Refresh tool tags
      queryClient.invalidateQueries({ queryKey: ["tool-tags"] });
      
      return newTool.id;
    },
    onSuccess: () => {
      setSearchInput("");
      setOpen(false);
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to add custom tool: ${error.message}`);
    },
  });

  // Remove tool mutation
  const removeToolMutation = useMutation({
    mutationFn: async (toolId: string) => {
      const { error } = await supabase
        .from("contributor_tools")
        .delete()
        .eq("id", toolId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to remove tool: ${error.message}`);
    },
  });

  // Filter out already added tools
  const availableTools = allTools?.filter(
    tool => !tools.some(t => t.tool_tag_id === tool.id)
  ) || [];

  // Group tools by category
  const groupedAvailableTools = CATEGORY_ORDER.reduce((acc, category) => {
    const categoryTools = availableTools.filter(t => t.category === category);
    if (categoryTools.length > 0) {
      acc[category] = categoryTools;
    }
    return acc;
  }, {} as Record<string, ToolTag[]>);

  // Check if search term exactly matches any available tool name
  const searchExactMatch = availableTools.some(
    tool => tool.name.toLowerCase() === searchInput.trim().toLowerCase()
  );

  const renderProficiencyStars = (level: string) => {
    const levelConfig = PROFICIENCY_LEVELS.find(l => l.value === level);
    if (!levelConfig) return null;
    
    return (
      <span className="ml-1 inline-flex">
        {Array.from({ length: levelConfig.stars }).map((_, i) => (
          <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
        ))}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      design: "Design",
      productivity: "Productivity",
      development: "Development",
      communication: "Communication",
      ai: "AI Tools",
      other: "Other",
    };
    return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const isAdding = addToolMutation.isPending || createCustomToolMutation.isPending;

  return (
    <div className="space-y-3">
      {/* Current tools with inline proficiency editing */}
      <div className="flex flex-wrap gap-2">
        {tools.map(tool => (
          <Popover key={tool.id}>
            <PopoverTrigger asChild>
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-secondary/80 transition-colors pr-1"
              >
                <Wrench className="h-3 w-3 opacity-60 mr-1" />
                <span>{tool.tool_tags.name}</span>
                {tool.proficiency && renderProficiencyStars(tool.proficiency)}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeToolMutation.mutate(tool.id);
                  }}
                  className="ml-1 p-0.5 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-2" align="start">
              <p className="text-xs font-medium mb-2 text-muted-foreground">Proficiency</p>
              <div className="space-y-1">
                {PROFICIENCY_LEVELS.map((level) => (
                  <button
                    key={level.value}
                    onClick={() => updateProficiencyMutation.mutate({ 
                      toolId: tool.id, 
                      proficiency: level.value 
                    })}
                    className={cn(
                      "w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between",
                      "hover:bg-accent transition-colors",
                      tool.proficiency === level.value && "bg-accent"
                    )}
                  >
                    <span>{level.label}</span>
                    <span className="inline-flex">
                      {Array.from({ length: level.stars }).map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                      ))}
                    </span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ))}

        {/* Add tool button */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-6 gap-1">
              {isAdding ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              Add Tool
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput 
                placeholder="Search tools..." 
                value={searchInput}
                onValueChange={setSearchInput}
              />
              <CommandList>
                <CommandEmpty>
                  {searchInput.trim().length >= 2 && !searchExactMatch && (
                    <button
                      onClick={() => createCustomToolMutation.mutate(searchInput)}
                      className="w-full px-2 py-3 text-sm text-left hover:bg-accent flex items-center gap-2"
                      disabled={createCustomToolMutation.isPending}
                    >
                      <Plus className="h-4 w-4" />
                      Add "{normalizeEntryName(searchInput)}" as new tool
                    </button>
                  )}
                  {searchInput.trim().length < 2 && (
                    <p className="py-3 text-center text-sm text-muted-foreground">
                      Type to search or add custom tool
                    </p>
                  )}
                </CommandEmpty>
                {Object.entries(groupedAvailableTools).map(([category, categoryTools]) => (
                  <CommandGroup key={category} heading={getCategoryLabel(category)}>
                    {categoryTools.map(tool => (
                      <CommandItem
                        key={tool.id}
                        value={tool.name}
                        onSelect={() => {
                          // One-click add with default "intermediate" proficiency
                          addToolMutation.mutate({ 
                            toolTagId: tool.id, 
                            proficiency: "intermediate" 
                          });
                        }}
                        className="cursor-pointer"
                      >
                        {tool.name}
                        {tool.is_custom && (
                          <span className="ml-1 text-xs text-muted-foreground">(custom)</span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {tools.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No tools added yet. Click "Add Tool" to help match you with projects.
        </p>
      )}
    </div>
  );
};